/**
 * The dealership settings context object and its hook, apart from the
 * provider (contexts/DealershipSettingsContext.js), so the many components
 * that only READ settings do not pull in the API client. With no provider
 * above them they read the built-in defaults, i.e. today's behaviour.
 */
import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DEFAULT_SETTINGS, resolveSettings } from '../utils/dealershipSettings';

export const NO_PROVIDER = Object.freeze({
    ...DEFAULT_SETTINGS,
    loading: false,
    loaded: false,
    error: null,
    reload: async () => null,
    updating: false,
    updateError: null,
    applyAfterRefresh: async (refresh) => (await refresh()) !== false,
    retryUpdate: async () => false,
});

export const DealershipSettingsContext = createContext(NO_PROVIDER);

/**
 * { values, aging: { boundaries, buckets, criticalDays }, criticalDays,
 *   gapThresholds, peerOverpricedPct, marginFloorPct, aprPct, canEdit,
 *   version, orgId, overridden, schema, loading, error, reload }.
 */
export function useDealershipSettings() {
    return useContext(DealershipSettingsContext);
}

/** For tests and previews: a fixed settings value without a request. */
export function StaticDealershipSettings({ payload = null, children }) {
    const value = useMemo(() => ({
        ...NO_PROVIDER,
        ...resolveSettings(payload),
        loaded: Boolean(payload),
        reload: async () => payload,
    }), [payload]);
    return <DealershipSettingsContext.Provider value={value}>{children}</DealershipSettingsContext.Provider>;
}

StaticDealershipSettings.propTypes = { payload: PropTypes.object, children: PropTypes.node };
